import React, { createContext, useContext, useState, useCallback, ReactNode, ComponentType } from 'react';
import ToastNotificationRaw from '@/components/ui/ToastNotification';
import { isReconnectingText } from '@/services/errors';

// ToastNotification is a .jsx file with implicit any props — cast it to a permissive component type
// so TSX usage type-checks until ToastNotification itself is migrated to .tsx.
const ToastNotification = ToastNotificationRaw as unknown as ComponentType<Record<string, unknown>>;

type ToastType = 'success' | 'error' | 'warning' | 'info' | 'progress';
type ToastPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';

interface ToastConfig {
  title?: string;
  description?: string;
  message?: string;
}

interface ToastOptions {
  duration?: number;
  position?: ToastPosition;
  autoClose?: boolean;
  onClick?: () => void;
  onCancel?: () => void;
  progress?: number;
}

interface Toast {
  id: number;
  title?: string;
  description?: string;
  message?: string;
  type: ToastType;
  duration: number;
  position: ToastPosition;
  autoClose: boolean;
  onClick?: () => void;
  onCancel?: () => void;
  progress?: number;
}

type ToastInput = string | ToastConfig;

/** Id returned when a toast is suppressed; `removeToast` on it is a no-op. */
export const SUPPRESSED_TOAST_ID = -1;

/**
 * JOSH-89: an error toast built from a SESSION_RECONNECTING answer is dropped.
 * The request never ran, the screen keeps its data, the reconnecting state has
 * its own indicator, and save forms show the message inline (`errors.submit`).
 */
export function isReconnectingToast(messageOrConfig: ToastInput): boolean {
  if (typeof messageOrConfig === 'string') return isReconnectingText(messageOrConfig);
  return (
    isReconnectingText(messageOrConfig.title) ||
    isReconnectingText(messageOrConfig.description) ||
    isReconnectingText(messageOrConfig.message)
  );
}

interface ToastContextValue {
  addToast: (messageOrConfig: ToastInput, type?: ToastType, options?: ToastOptions) => number;
  removeToast: (id: number) => void;
  removeAllToasts: () => void;
  updateToast: (id: number, changes: Partial<Toast>) => void;
  showSuccess: (messageOrConfig: ToastInput, options?: ToastOptions) => number;
  showError: (messageOrConfig: ToastInput, options?: ToastOptions) => number;
  showWarning: (messageOrConfig: ToastInput, options?: ToastOptions) => number;
  showInfo: (messageOrConfig: ToastInput, options?: ToastOptions) => number;
  showProgress: (messageOrConfig: ToastInput, options?: ToastOptions) => number;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  /**
   * Adiciona um toast
   *
   * Exemplos de uso:
   *   addToast('Mensagem simples', 'success')
   *   addToast({ title: 'Título', description: 'Descrição' }, 'error')
   */
  const addToast = useCallback((messageOrConfig: ToastInput, type: ToastType = 'info', options: ToastOptions = {}) => {
    if (type === 'error' && isReconnectingToast(messageOrConfig)) return SUPPRESSED_TOAST_ID;
    const id = Date.now() + Math.random();

    // Normalizar para suportar string ou objeto
    let title: string | undefined;
    let description: string | undefined;
    let message: string | undefined;
    if (typeof messageOrConfig === 'string') {
      message = messageOrConfig;
    } else if (typeof messageOrConfig === 'object') {
      title = messageOrConfig.title;
      description = messageOrConfig.description;
      message = messageOrConfig.message;
    }

    const toast: Toast = {
      id,
      title,
      description,
      message,
      type,
      duration: options.duration || 5000,
      position: options.position || 'top-right',
      autoClose: options.autoClose !== false,
      onClick: options.onClick,
      onCancel: options.onCancel,
      progress: options.progress
    };

    setToasts(prev => [...prev, toast]);

    // Auto-remove toast after duration
    if (toast.autoClose) {
      setTimeout(() => {
        removeToast(id);
      }, toast.duration);
    }

    return id;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- removeToast intentionally omitted to avoid circular dependency between addToast and removeToast
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const removeAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const updateToast = useCallback((id: number, changes: Partial<Toast>) => {
    setToasts(prev => prev.map(t =>
      t.id === id ? { ...t, ...changes } : t
    ));
    if (changes.autoClose && changes.duration) {
      setTimeout(() => removeToast(id), changes.duration);
    }
  }, [removeToast]);

  // Convenience methods - aceitam string ou { title, description }
  const showSuccess = useCallback((messageOrConfig: ToastInput, options?: ToastOptions) => {
    return addToast(messageOrConfig, 'success', options);
  }, [addToast]);

  const showError = useCallback((messageOrConfig: ToastInput, options?: ToastOptions) => {
    return addToast(messageOrConfig, 'error', options);
  }, [addToast]);

  const showWarning = useCallback((messageOrConfig: ToastInput, options?: ToastOptions) => {
    return addToast(messageOrConfig, 'warning', options);
  }, [addToast]);

  const showInfo = useCallback((messageOrConfig: ToastInput, options?: ToastOptions) => {
    return addToast(messageOrConfig, 'info', options);
  }, [addToast]);

  const showProgress = useCallback((messageOrConfig: ToastInput, options?: ToastOptions) => {
    return addToast(messageOrConfig, 'progress', {
      autoClose: false,
      position: 'bottom-right',
      ...options
    });
  }, [addToast]);

  const value: ToastContextValue = {
    addToast,
    removeToast,
    removeAllToasts,
    updateToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showProgress
  };

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/* Render toasts */}
      {toasts.map((toast) => {
        const samePositionToasts = toasts.filter(t => (t.position || 'top-right') === (toast.position || 'top-right'));
        const posIndex = samePositionToasts.indexOf(toast);

        return (
          <ToastNotification
            key={toast.id}
            title={toast.title}
            message={toast.message}
            description={toast.description}
            type={toast.type}
            duration={toast.duration}
            position={toast.position}
            autoClose={toast.autoClose}
            progress={toast.progress}
            stackIndex={posIndex}
            onClose={() => {
              if (toast.onCancel) toast.onCancel();
              removeToast(toast.id);
            }}
            onClick={toast.onClick}
          />
        );
      })}
    </ToastContext.Provider>
  );
};

export default ToastProvider;
